import { useState, useCallback } from 'react';
import authService from '../../../services/authService';

/**
 * Custom hook for managing file operations and modal functionality
 */
export const useFileOperations = (pentestServer, addProgressLog) => {
  const [resultFiles, setResultFiles] = useState([]);
  const [showFileModal, setShowFileModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileContent, setFileContent] = useState('');
  const [fileContentType, setFileContentType] = useState('text');
  const [isLoadingFile, setIsLoadingFile] = useState(false);

  // Function to fetch result files from the server
  const fetchResultFiles = useCallback(async (scanId = null) => {
    try {
      const url = scanId
        ? `${pentestServer}/results/files/${scanId}`
        : `${pentestServer}/results/files`;
      
      const response = await authService.authenticatedCall(url);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const ct = response.headers.get('content-type') || '';
      if (!ct.includes('application/json')) {
        const text = await response.text();
        throw new Error(`Unexpected response (expected JSON). First 120 chars: ${text.slice(0, 120)}`);
      }
      const data = await response.json();
      setResultFiles(data.files || []);
    } catch (error) {
      console.error("Could not fetch result files:", error);
      if (addProgressLog) {
        addProgressLog(`Error fetching result files: ${error.message}`, 'error');
      }
    }
  }, [pentestServer, addProgressLog]);

  // Function to view file content
  const viewFileContent = useCallback(async (file) => {
    setIsLoadingFile(true);
    setSelectedFile(file);
    setShowFileModal(true);

    try {
      const url = file.scanId
        ? `${pentestServer}/results/file/${file.scanId}/${file.name}`
        : `${pentestServer}/results/file/${file.name}`;

      const ext = file.name.split('.').pop().toLowerCase();

      let content, fileType;

      if (ext === 'pdf') {
        const response = await authService.authenticatedCall(url);
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        content = await response.blob();
        fileType = 'pdf';
      } else {
        const response = await authService.authenticatedCall(url);
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        content = await response.text();

        if (ext === 'json') {
          fileType = 'json';
        } else if (ext === 'html' || ext === 'htm') {
          fileType = 'html';
        } else if (ext === 'xml') {
          fileType = 'xml';
        } else if (['log', 'txt'].includes(ext)) {
          fileType = 'log';
        } else if (['md', 'markdown'].includes(ext)) {
          fileType = 'markdown';
        } else {
          fileType = 'text';
        }
      }

      setFileContent(content);
      setFileContentType(fileType);
    } catch (error) {
      console.error("Could not fetch file content:", error);
      setFileContent(`Error loading file: ${error.message}`);
      if (addProgressLog) {
        addProgressLog(`Error viewing file content: ${error.message}`, 'error');
      }
    } finally {
      setIsLoadingFile(false);
    }
  }, [pentestServer, addProgressLog]);

  // Function to close file modal
  const closeFileModal = useCallback(() => {
    setShowFileModal(false);
    setSelectedFile(null);
    setFileContent('');
    setFileContentType('text');
  }, []);

  return {
    resultFiles,
    showFileModal,
    selectedFile,
    fileContent,
    fileContentType,
    isLoadingFile,
    fetchResultFiles,
    viewFileContent,
    closeFileModal,
    setResultFiles
  };
};